import type { FramingBox } from "./framingInspector";

// ---------------------------------------------------------------------------
// manifestFraming — guarded reads of `scenes[].framing_plan` from the
// echo-state manifest for the Director View overlay (QCSV-007).
//
// The manifest carries the composer's FramingPlan model dump: normalized boxes
// are `{x1, y1, x2, y2}` (converted here to the CRM's `FramingBox` shape so
// `framingOverlayMath.projectNormalizedBox` is reusable verbatim), the
// decision under `framing_plan.decision`, and per-render receipts under
// `framing_plan.applied_receipts`. Framing plans exist only on echoes rendered
// with framing telemetry active (cohort observe/enforce) — absence is the
// normal case and never an error.
// ---------------------------------------------------------------------------

export type SceneFramingOverlay = {
  sceneId: number | string | null;
  /** Scene position (matches SyncScene.index / resolveSceneSources keys). */
  index: number;
  strategy: string | null;
  /** The framing decision's crop window (planned geometry). */
  cropWindow: FramingBox | null;
  /** The latest applied receipt's crop window (what a renderer actually did). */
  appliedCropWindow: FramingBox | null;
  appliedStatus: string | null;
  fallbackReason: string | null;
  /** Fraction of the source frame the decision window keeps (0..1). */
  retainedAreaFraction: number | null;
  /** Moving-crop caveat: Ken Burns / tracking paths animate the window. */
  hasCropPath: boolean;
  sourceWidth: number | null;
  sourceHeight: number | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * Parse a manifest normalized box (`{x1, y1, x2, y2}` in [0,1]) into a
 * `FramingBox`. Inverted, degenerate, or out-of-range boxes are rejected —
 * drawing a wrong rectangle is worse than drawing none.
 */
export function parseManifestBox(value: unknown): FramingBox | null {
  if (!isRecord(value)) return null;
  const x1 = num(value.x1);
  const y1 = num(value.y1);
  const x2 = num(value.x2);
  const y2 = num(value.y2);
  if (x1 == null || y1 == null || x2 == null || y2 == null) return null;
  if (x1 < 0 || y1 < 0 || x2 > 1 || y2 > 1) return null;
  if (x2 <= x1 || y2 <= y1) return null;
  return { xMin: x1, yMin: y1, xMax: x2, yMax: y2 };
}

function boxArea(box: FramingBox | null): number | null {
  if (!box) return null;
  return (box.xMax - box.xMin) * (box.yMax - box.yMin);
}

type ReceiptPick = {
  cropWindow: FramingBox | null;
  status: string | null;
  sourceWidth: number | null;
  sourceHeight: number | null;
};

function pickReceipt(receipts: unknown): ReceiptPick | null {
  if (!Array.isArray(receipts) || receipts.length === 0) return null;
  // Prefer the last APPLIED receipt (renderers append), falling back to the
  // last receipt of any status so fallback geometry still shows with a chip.
  const records = receipts.filter(isRecord);
  const applied = [...records]
    .reverse()
    .find((receipt) => str(receipt.status) === "applied");
  const chosen = applied ?? records.at(-1);
  if (!chosen) return null;
  return {
    cropWindow: parseManifestBox(chosen.crop_window),
    status: str(chosen.status),
    sourceWidth: num(chosen.source_width),
    sourceHeight: num(chosen.source_height),
  };
}

function inputRawDims(spaces: unknown): {
  width: number | null;
  height: number | null;
} {
  if (!Array.isArray(spaces)) return { width: null, height: null };
  for (const raw of spaces) {
    if (!isRecord(raw)) continue;
    if (str(raw.stage) === "input_raw") {
      return { width: num(raw.width), height: num(raw.height) };
    }
  }
  return { width: null, height: null };
}

/**
 * Extract per-scene overlay data from a parsed echo-state manifest. Keys are
 * scene INDEXES (positions in `scenes[]`, which mirror the script order).
 * Returns an empty map when no scene has a framing plan.
 */
export function parseManifestFraming(
  manifest: unknown,
): Map<number, SceneFramingOverlay> {
  const byIndex = new Map<number, SceneFramingOverlay>();
  if (!isRecord(manifest) || !Array.isArray(manifest.scenes)) return byIndex;

  manifest.scenes.forEach((rawScene, position) => {
    if (!isRecord(rawScene)) return;
    const plan = rawScene.framing_plan;
    if (!isRecord(plan)) return;
    const decision = isRecord(plan.decision) ? plan.decision : null;
    if (!decision) return;

    const index = num(rawScene.index) ?? position;
    const cropWindow = parseManifestBox(decision.crop_window);
    const cropPath = Array.isArray(decision.crop_path)
      ? decision.crop_path
      : [];
    const receipt = pickReceipt(plan.applied_receipts);
    const spaceDims = inputRawDims(plan.spaces);
    const sceneIdRaw = rawScene.scene_id ?? rawScene.id;

    byIndex.set(index, {
      sceneId:
        typeof sceneIdRaw === "number" || typeof sceneIdRaw === "string"
          ? sceneIdRaw
          : null,
      index,
      strategy: str(decision.strategy),
      cropWindow,
      appliedCropWindow: receipt?.cropWindow ?? null,
      appliedStatus: receipt?.status ?? null,
      fallbackReason: str(decision.fallback_reason),
      retainedAreaFraction: boxArea(cropWindow),
      hasCropPath: cropPath.length > 0,
      sourceWidth: receipt?.sourceWidth ?? spaceDims.width,
      sourceHeight: receipt?.sourceHeight ?? spaceDims.height,
    });
  });

  return byIndex;
}
